import { 
  Component, 
  OnInit,
  OnChanges,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
  NgZone,
  Inject
} from '@angular/core';

import {
  ChartComponent,
  ApexTitleSubtitle,
  ApexChart,
  ApexLegend,
  ApexTooltip,
  ApexDataLabels,
  ApexStroke,
  ApexTheme
} from 'ng-apexcharts';

import { isNil } from 'lodash-es';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { Waves_ShellService } from './Waves.shell.service';
import { Waves_OperationService } from './Waves.operation.service';
import { Waves_DatasourceService } from './Waves.datasource.index';
import { Waves_FlowService } from './Waves.flow.index';
import { Waves_ReportService } from './Waves.report.index';
import { Waves_LocalizationService } from './Waves.localization.service';
import { Waves_pick_waves_widget_ComponentContextService } from './Waves.pick_waves_widget.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './Waves.frontend.types'
import { $frontendTypes as $types} from './Waves.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'Waves-pick_waves_widget',
  templateUrl: './Waves.pick_waves_widget.component.html'
})
export class Waves_pick_waves_widgetComponent extends BaseComponent implements OnInit, OnChanges {
  //#region Outputs
  @Output()
  outParamsChange = new EventEmitter<{ slice?: { entity?: { WaveCount?: number, Status?: { Id?: number, Name?: string }, StatusName?: string } } }>();
  @Output()
  $refreshEvent = new EventEmitter();
  //#endregion Outputs

  inParams: { projectId?: number, ownerId?: number, warehouseId?: number, statusIds?: number[] } = { projectId: null, ownerId: null, warehouseId: null, statusIds: [] };
  //#region Inputs
  @Input('projectId') set $inParams_projectId(v: number) {
    this.inParams.projectId = v;
  }
  get $inParams_projectId(): number {
    return this.inParams.projectId;
  }
  @Input('ownerId') set $inParams_ownerId(v: number) {
    this.inParams.ownerId = v;
  }
  get $inParams_ownerId(): number {
    return this.inParams.ownerId;
  }
  @Input('warehouseId') set $inParams_warehouseId(v: number) {
    this.inParams.warehouseId = v;
  }
  get $inParams_warehouseId(): number {
    return this.inParams.warehouseId;
  }
  @Input('statusIds') set $inParams_statusIds(v: number[]) {
    this.inParams.statusIds = v;
  }
  get $inParams_statusIds(): number[] {
    return this.inParams.statusIds;
  }
  //#endregion Inputs

  //#region Variables
  //#endregion
  @ViewChild('pieChart') 
  pieChart: ChartComponent;

  private selected = false;
  private entities: { WaveCount?: number, Status?: { Id?: number, Name?: string }, StatusName?: string }[];

  series: number[] = [];
  labels: string[] = [];

  chart: ApexChart = {
    type: 'pie',
    width: '100%',
    height: '170px', // fixed heigh because of issues with ApexCharts not being able to respect the container height
    redrawOnParentResize: true,
    fontFamily: '"Segoe UI", "Roboto", sans-serif',
    events: {
      dataPointSelection: (event, chartContext, config) => {
        // run inside the Angular zone, since ng-apexcharts runs almost everything outside of it
        // see: https://github.com/apexcharts/ng-apexcharts/blob/master/projects/ng-apexcharts/src/lib/chart/chart.component.ts
        this.ngZone.run(() => {
          if (this.isPiePointSelected(config.dataPointIndex)) {
            this.outParamsChange.emit({ slice: null});
          } else {
            this.outParamsChange.emit({ slice: { entity: this.entities[config.dataPointIndex] } } );
          }
        });
      }
    }
  };
  dataLabels: ApexDataLabels = {
    enabled: false,
  };
  stroke: ApexStroke = {
    width: 0
  };
  title: ApexTitleSubtitle = {
  };
  legend: ApexLegend = {
    show: false
  };
  tooltip: ApexTooltip = {
    followCursor: false,
    fillSeriesColor: true,
    onDatasetHover: {
      highlightDataSeries: true,
    },
    style: {
      fontSize: '14px',
      fontFamily: '"Segoe UI", "Roboto", sans-serif',
    },
    enabled: true
  };
  theme: ApexTheme = {
    palette: 'palette2',
  };

  constructor(
    private $utils: UtilsService,
    private $settings: SettingsValuesService,
    private $shell: Waves_ShellService,
    private $datasources: Waves_DatasourceService,
    private $flows: Waves_FlowService,
    private $reports: Waves_ReportService,
    private $localization: Waves_LocalizationService,
    private $operations: Waves_OperationService,
    private $logger: CleanupLoggerService,
    private $context: Waves_pick_waves_widget_ComponentContextService,
    private ngZone: NgZone
  ) { 
    super();
  }

  ngOnInit(): void {
    this.$init();
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$init();
    }
  }


  initialized = false;

  async $init() {
    
    await this.$dataLoad();

    this.initialized = true;
  }
  
  async $dataLoad() {
    const $widget = this;
    const $utils = this.$utils;

    const dsParams = {
      ownerId:  $widget.inParams.ownerId ,
      projectIds:  $widget.inParams.projectId ,
      statusIds:  $widget.inParams.statusIds ,
      warehouseId:  $widget.inParams.warehouseId 
    };

    const data = await this.$datasources.Waves.ds_open_waves_widget.getList(dsParams);
    this.entities = data.result;
    await this.$dataLoaded();
  }

   async $dataLoaded() {
    const $widget = this;
    const $utils = this.$utils;

    if(this.entities) {
      this.labels = this.entities.map(e => { 
        const $slice = { entity:  e };
        return $slice.entity.StatusName;
      });

      this.series = this.entities.map(e => { 
        const $slice = { entity:  e };
        return $slice.entity.WaveCount;
      });
    }

  }

  refresh(
    skipParent = false,
    skipChildren = false,
    childToSkip: string = null) {
    // up
    if (skipParent === false) {
      this.$refreshEvent.emit();
    }

    // self
    const result = this.$dataLoad();

    // children
    if (skipChildren === false) {
    }

    return result;
  }

  private isPiePointSelected(pointIndex: number) {
    if (this.pieChart) {
      const pieSlice = (this.pieChart as any).chartObj.w.globals.dom.Paper.select(`.apexcharts-pie-slice-${pointIndex}`).members[0];
      const isSelected = pieSlice.attr('data:pieClicked');
      return isSelected === 'true';
    }
    return false;
  }

}
